import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_ENDPOINTS } from '../config/api-endpoints';

export interface CustomerSession {
  id: number;
  sessionId: string;
  tableNumber: string;
  customerName?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  barId: string;
  status: string;
  isActive: boolean;
  totalAmount: number;
  itemCount: number;
  notes?: string | null;
  orderId?: number | null;
  orderInvoice?: string | null;
  lastOrderTotal?: number | null;
  expiresAt?: string | null;
  lastActivity?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CartItem {
  id: number;
  sessionId: string;
  barId: string;
  drinkId: number;
  drinkName: string;
  category: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  specialInstructions?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateSessionRequest {
  customerName?: string;
  phoneNumber?: string;
  email?: string;
  notes?: string;
}

export interface PlaceOrderResponse {
  orderId: number;
  invoiceNumber: string;
  total: number;
  status: string;
}

export interface InitiatePaymentResponse {
  redirectUrl: string;
  transactionId: string;
}

export interface FeedbackRequest {
  // Field names must match CustomerFeedback on the server. They were `rating`
  // and `comment`, which the entity does not declare — and since overallRating
  // is @NotNull with @Min(1)/@Max(5), every submission failed validation. No
  // feedback was ever stored, which is why bar ratings never moved off zero.
  overallRating: number;
  comments: string;
  customerName?: string;
  type?: string;
}

export interface CustomerDashboard {
  totalVisits: number;
  totalSpent: number;
  lastVisit: string;
  favouriteDrink?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  constructor(private http: HttpClient) { }

  createSession(tableNumber: string, barId: string): Observable<CustomerSession> {
    const url = `customer:${API_ENDPOINTS.CUSTOMER.SESSIONS}`;
    return this.http.post<CustomerSession>(url, null, {
      params: { tableNumber, barId }
    });
  }

  getSession(sessionId: string): Observable<CustomerSession> {
    const url = `customer:${API_ENDPOINTS.CUSTOMER.SESSIONS_BY_ID(sessionId)}`;
    return this.http.get<CustomerSession>(url);
  }

  updateSession(sessionId: string, data: UpdateSessionRequest): Observable<CustomerSession> {
    return this.http.put<CustomerSession>(
      `customer:${API_ENDPOINTS.CUSTOMER.SESSIONS_BY_ID(sessionId)}`, data
    );
  }

  getCart(sessionId: string): Observable<CartItem[]> {
    return this.http.get<CartItem[]>(`customer:${API_ENDPOINTS.CUSTOMER.CART(sessionId)}`);
  }

  addToCart(sessionId: string, drinkId: number, quantity: number, specialInstructions?: string): Observable<CartItem> {
    const params: any = { drinkId: drinkId.toString(), quantity: quantity.toString() };
    if (specialInstructions) params.specialInstructions = specialInstructions;
    return this.http.post<CartItem>(`customer:${API_ENDPOINTS.CUSTOMER.CART(sessionId)}`, null, { params });
  }

  updateCartItem(sessionId: string, cartItemId: number | string, quantity: number): Observable<CartItem> {
    return this.http.put<CartItem>(
      `customer:${API_ENDPOINTS.CUSTOMER.CART_ITEM(sessionId, cartItemId)}`, null,
      { params: { quantity: quantity.toString() } }
    );
  }

  removeCartItem(sessionId: string, cartItemId: number | string): Observable<void> {
    return this.http.delete<void>(`customer:${API_ENDPOINTS.CUSTOMER.CART_ITEM(sessionId, cartItemId)}`);
  }

  clearCart(sessionId: string): Observable<void> {
    return this.http.delete<void>(`customer:${API_ENDPOINTS.CUSTOMER.CART(sessionId)}`);
  }

  getMenu(sessionId: string, category?: string): Observable<any[]> {
    const params: any = {};
    if (category) params.category = category;
    return this.http.get<any[]>(`customer:${API_ENDPOINTS.CUSTOMER.MENU(sessionId)}`, { params });
  }

  placeOrder(sessionId: string): Observable<PlaceOrderResponse> {
    return this.http.post<PlaceOrderResponse>(
      `customer:${API_ENDPOINTS.CUSTOMER.SESSION_ORDERS(sessionId)}`, null
    );
  }

  initiatePayment(sessionId: string, paymentMethod: string): Observable<InitiatePaymentResponse> {
    return this.http.post<InitiatePaymentResponse>(
      `customer:${API_ENDPOINTS.CUSTOMER.SESSION_PAYMENTS(sessionId)}`, null,
      { params: { paymentMethod } }
    );
  }

  /**
   * Settle the payment after the customer returns from the provider's hosted
   * page. Sends no outcome: the server asks the provider, because anything the
   * browser came back with could have been typed by hand.
   */
  completeRedirectPayment(sessionId: string): Observable<{ transactionId: string; status: string; amount: number }> {
    return this.http.post<{ transactionId: string; status: string; amount: number }>(
      `customer:${API_ENDPOINTS.CUSTOMER.SESSION_PAYMENT_COMPLETE(sessionId)}`, null
    );
  }

  submitFeedback(sessionId: string, feedback: FeedbackRequest): Observable<void> {
    return this.http.post<void>(
      `customer:${API_ENDPOINTS.CUSTOMER.SESSION_FEEDBACK(sessionId)}`, feedback
    );
  }

  getAvailableTables(): Observable<string[]> {
    return this.http.get<string[]>(`customer:${API_ENDPOINTS.CUSTOMER.TABLES_AVAILABLE}`);
  }

  scanQr(qrData: string): Observable<CustomerSession> {
    return this.http.post<CustomerSession>(
      `customer:${API_ENDPOINTS.CUSTOMER.QR_SCAN}`, null,
      { params: { qrData } }
    );
  }

  getMyDashboard(): Observable<CustomerDashboard> {
    return this.http.get<CustomerDashboard>(`customer:${API_ENDPOINTS.CUSTOMER.MY_DASHBOARD}`);
  }

  getVisitHistory(period: 'WEEK' | 'MONTH' | 'YEAR'): Observable<any[]> {
    return this.http.get<any[]>(`customer:${API_ENDPOINTS.CUSTOMER.MY_HISTORY_VISITS}`, {
      params: { period }
    });
  }

  getExpenditureHistory(period: 'WEEK' | 'MONTH' | 'YEAR'): Observable<any[]> {
    return this.http.get<any[]>(`customer:${API_ENDPOINTS.CUSTOMER.MY_HISTORY_EXPENDITURE}`, {
      params: { period }
    });
  }
}
